import type { CeilingFinishId, Design, FloorFinishId, RoomSetting, WallFinish, WallFinishId } from "../../types";
import {
  ceilingFinish,
  finishSurfaceFor,
  floorFinish,
  wallFinish,
  type FinishGeometry,
} from "../../finishes/catalog";
import {
  DEFAULT_DOOR,
  DEFAULT_EXTERIOR,
  effectiveFinishes,
  longestWallAngle,
  roomKind,
} from "../../finishes/effective";
import type { MaterialSpec, SurfaceId } from "../materials/catalog3d";
import type { Render3dFloorStrip } from "../types";

export { longestWallAngle, roomKind };

/**
 * The room's finishes as 3D surfaces: what the user chose (valid for the
 * setting) over what we'd infer, per wall where a wall has its own. Every
 * surface carries a material `slot` so a color change recolors the shared
 * material in place; only a type change rebuilds the shell.
 */

export interface ResolvedSurface {
  surface: SurfaceId;
  color: string;
  slot: string;
}

export interface ResolvedWall {
  type: WallFinishId;
  geometry: FinishGeometry;
  /** The wall's face (felt backing behind slats). */
  face: ResolvedSurface;
  /** Slats / battens; null for flat finishes. */
  cladding: ResolvedSurface | null;
  /** Outdoor boundary height in inches; null = ceiling height. */
  height: number | null;
}

export interface ResolvedCeiling extends ResolvedSurface {
  type: CeilingFinishId;
  geometry: FinishGeometry;
  /** Beams / pergola rafters. */
  beam: ResolvedSurface | null;
  /** Plank direction in the plan, radians. */
  angle: number;
}

export interface RoomFinishes {
  setting: RoomSetting;
  floor: ResolvedSurface & { type: FloorFinishId; angle: number };
  walls: ResolvedWall;
  wallOverrides: Record<string, ResolvedWall>;
  /** Null under an open sky. */
  ceiling: ResolvedCeiling | null;
  trim: ResolvedSurface;
  door: ResolvedSurface;
  exterior: ResolvedSurface;
  /** Review gallery: floors laid side by side instead of `floor`. */
  floorStrips?: (ResolvedSurface & { type: FloorFinishId })[];
}

/** Acoustic felt behind wood slats. */
export const SLAT_BACKING = "#262422";
/** The flat ceiling between beams. */
export const BEAM_CEILING = "#f6f5f2";

/** Slots are per wall, so one wall can light up (or take its own color) alone. */
function wallSlots(w: ResolvedWall, key: string): ResolvedWall {
  const slot = `wall:${key}`;
  return {
    ...w,
    face: { ...w.face, slot: w.geometry === "slat" ? `${slot}:felt` : slot },
    cladding: w.cladding && { ...w.cladding, slot: `${slot}:clad` },
  };
}

function resolveWall(finish: WallFinish): ResolvedWall {
  const option = wallFinish(finish.type);
  const surface = finishSurfaceFor(option, finish.color);
  const base = { type: finish.type, geometry: option.geometry, height: option.height ?? null };
  if (option.geometry === "slat") {
    return {
      ...base,
      face: { surface: "wall-felt", color: SLAT_BACKING, slot: "" },
      cladding: { surface, color: finish.color, slot: "" },
    };
  }
  if (option.geometry === "panelling") {
    return {
      ...base,
      face: { surface, color: finish.color, slot: "" },
      cladding: { surface: "wall-panel", color: finish.color, slot: "" },
    };
  }
  return { ...base, face: { surface, color: finish.color, slot: "" }, cladding: null };
}

export function resolveRoomFinishes(
  design: Pick<Design, "room" | "furniture">,
  floorStrips?: readonly Render3dFloorStrip[]
): RoomFinishes {
  const eff = effectiveFinishes(design);
  const baseAngle = longestWallAngle(design.room.points);
  const floorOption = floorFinish(eff.floor.type);
  const across = floorOption.directional && eff.floor.direction === "across";
  const ceilingOption = ceilingFinish(eff.ceiling.type);
  let ceiling: ResolvedCeiling | null = null;
  if (ceilingOption.geometry === "beams") {
    ceiling = {
      type: eff.ceiling.type,
      geometry: "beams",
      surface: "ceiling-paint",
      color: BEAM_CEILING,
      slot: "ceiling",
      beam: { surface: ceilingOption.surface, color: eff.ceiling.color, slot: "ceiling:beam" },
      angle: baseAngle,
    };
  } else if (ceilingOption.geometry === "pergola") {
    ceiling = {
      type: eff.ceiling.type,
      geometry: "pergola",
      surface: ceilingOption.surface,
      color: eff.ceiling.color,
      slot: "ceiling",
      beam: { surface: ceilingOption.surface, color: eff.ceiling.color, slot: "ceiling:beam" },
      angle: baseAngle,
    };
  } else if (ceilingOption.geometry !== "open") {
    ceiling = {
      type: eff.ceiling.type,
      geometry: ceilingOption.geometry,
      surface: ceilingOption.surface,
      color: eff.ceiling.color,
      slot: "ceiling",
      // A covered patio's roof stands on posts and headers in its own color.
      beam: ceilingOption.geometry === "covered" ? { surface: "pergola-wood", color: eff.ceiling.color, slot: "ceiling:beam" } : null,
      angle: baseAngle,
    };
  }
  const wallOverrides: Record<string, ResolvedWall> = {};
  for (const [key, f] of Object.entries(eff.wallOverrides)) wallOverrides[key] = resolveWall(f);
  return {
    setting: eff.setting,
    floor: {
      type: eff.floor.type,
      surface: finishSurfaceFor(floorOption, eff.floor.color),
      color: eff.floor.color,
      slot: "floor",
      angle: baseAngle + (across ? Math.PI / 2 : 0),
    },
    walls: resolveWall(eff.walls),
    wallOverrides,
    ceiling,
    trim: { surface: "trim-paint", color: eff.trimColor, slot: "trim" },
    door: { surface: "door-paint", color: DEFAULT_DOOR, slot: "door" },
    exterior: { surface: "exterior-paint", color: DEFAULT_EXTERIOR, slot: "exterior" },
    ...(floorStrips?.length
      ? {
          floorStrips: floorStrips.map((s, i) => ({
            type: s.type,
            surface: finishSurfaceFor(floorFinish(s.type), s.color),
            color: s.color,
            slot: `floor:strip:${i}`,
          })),
        }
      : {}),
  };
}

/** One wall's finish (its own, else the room's), with that wall's material slots. */
export function wallFinishOf(finishes: RoomFinishes, key: string): ResolvedWall {
  return wallSlots(finishes.wallOverrides[key] ?? finishes.walls, key);
}

/** A surface as a material request. */
export function slotSpec(s: ResolvedSurface): MaterialSpec {
  return { surface: s.surface, color: s.color, slot: s.slot };
}

/** Every slotted surface in a room with these walls. */
export function finishSurfaces(finishes: RoomFinishes, wallKeys: string[]): ResolvedSurface[] {
  const out: ResolvedSurface[] = [finishes.floor, ...(finishes.floorStrips ?? []), finishes.trim, finishes.door, finishes.exterior];
  for (const key of wallKeys) {
    const w = wallFinishOf(finishes, key);
    out.push(w.face);
    if (w.cladding) out.push(w.cladding);
  }
  if (finishes.ceiling) {
    out.push(finishes.ceiling);
    if (finishes.ceiling.beam) out.push(finishes.ceiling.beam);
  }
  return out;
}

/** Material slots a picked surface lights up. */
export function pickSlots(pick: { surface: "floor" | "walls" | "ceiling"; wallKey: string | null }, wallKeys: string[]): string[] {
  if (pick.surface === "floor") return ["floor"];
  if (pick.surface === "ceiling") return ["ceiling", "ceiling:beam"];
  const keys = pick.wallKey ? [pick.wallKey] : wallKeys;
  return keys.flatMap((k) => [`wall:${k}`, `wall:${k}:felt`, `wall:${k}:clad`]);
}

/** Everything about the finishes except colors: when this changes, the shell rebuilds. */
export function finishesStructureKey(finishes: RoomFinishes): string {
  return JSON.stringify(finishes, (k, v) => (k === "color" ? undefined : v));
}
